import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthFlowStateEventType, AuthWorkFlow, AuthResult, PincodeNativeStatus } from './../../models/pin-code.model';
import { Injectable, Inject } from '@angular/core';
import { IonSlides } from '@ionic/angular';
import { AuthFlowState, AuthType, FlowType, ScreenType } from 'src/app/models/pin-code.model';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { VaultService } from 'src/app/services/vault/vault.service';
import { PincodeModalType } from '../pin-code/pin-code.service';
import { AppConfig } from 'src/app/app.config';
import { EventBusService, EventTopic } from '../event-bus/event-bus.service';

@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private _flowType: FlowType = FlowType.setup;
  private _type: AuthType = AuthType.pincode;
  private _currentScreen: ScreenType = ScreenType.optional;
  private _slides: IonSlides;
  slideOpts = { initialSlide: 0, speed: 400, allowTouchMove: false };

  loaded = false;
  pincode = {
    featureToggled: false,
    enabled: localStorage.getItem('pincodeStatus') === PincodeNativeStatus.enabled,
    prompted: localStorage.getItem('pincodeStatus') === PincodeNativeStatus.prompted,
    resetPrompted: localStorage.getItem('pincodeStatus') === PincodeNativeStatus.resetPrompted,
    skipped: localStorage.getItem('pincodeStatus') === PincodeNativeStatus.skipped,
  };
  native = {
    enabled: localStorage.getItem('nativeStatus') === PincodeNativeStatus.enabled,
    prompted: localStorage.getItem('nativeStatus') === PincodeNativeStatus.prompted,
    skipped: localStorage.getItem('nativeStatus') === PincodeNativeStatus.skipped,
  };

  pin = '';
  validatePin = '';
  retryCount = 0;
  _locked = false;
  resetToken: string; // reset token from the URL

  constructor(
    private vault: VaultService,
    public profileSvc: ProfileService,
    private storageSvc: StorageService,
    private authSvc: AuthService,
    private eventBusSvc: EventBusService,
    @Inject(AppConfig) private config: AppConfig
  ) {}

  get flowType(): FlowType {
    return this._flowType;
  }
  get type(): AuthType {
    return this._type;
  }
  get currentScreen(): ScreenType {
    return this._currentScreen;
  }
  set locked(locked: boolean) {
    if (this._locked !== locked) {
      this._locked = locked;
      this.resetToken = null;
      if (this._locked) {
        this.vault.lockVault();
        this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.updateWorkflow });
      }
    }
  }
  get locked(): boolean {
    return this._locked;
  }

  set slides(slides: IonSlides) {
    this._slides = slides;
    this._slides.ionSlideNextEnd.subscribe(async e => {
      this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.nextState });
    });
    this._slides.ionSlidePrevEnd.subscribe(async e => {
      this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.prevState });
    });
  }

  get slides(): IonSlides {
    return this._slides;
  }

  async init() {
    if (!this.profileSvc.profile) {
      await this.storageSvc.get('token');
      await this.profileSvc.getProfile();
    }
    this.clearPin();
    this.setPinStatus();
    this.retryCount = this.profileSvc.profile?.pin_code_attempts_left;
    this.loaded = true;
  }

  setPinStatus() {
    const apiPinStatus = this.profileSvc.profile?.pin_code_status;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    this.pincode.featureToggled = this.profileSvc.profile?.pin_code_enabled && this.config['PIN_FEATURE'] === 'optional';
    this.pincode.enabled = apiPinStatus === 'set';
    this.pincode.prompted = apiPinStatus === 'prompted';
    this.pincode.skipped = apiPinStatus === 'skipped';
    this.locked = apiPinStatus === 'blocked';
  }

  async enterPin(pin: string): Promise<AuthResult> {
    try {
      switch (this.currentScreen) {
        case ScreenType.change:
        case ScreenType.create:
          this.pin = pin;
          await this.next();
          break;
        case ScreenType.validate:
          this.validatePin = pin;
          if (this.pin !== this.validatePin) {
            this.validatePin = '';
            return AuthResult.doesntMatch;
          }
          if (this.resetToken) {
            await this.vault.resetPin(this.pin, this.resetToken);
          } else {
            await this.vault.setPin(this.pin);
          }
          await this.profileSvc.getProfile();
          await this.setStatus();
          this.setPinStatus();
          await this.next();
          break;
        case ScreenType.login:
          await this.vault.validatePin(pin);
          await this.setStatus();
          this.retryCount = 5;
          await this.next();
          break;
      }
    } catch ({ error }) {
      if (error && 'attempts_left' in error) {
        this.retryCount = Number(error.attempts_left);
        this.vault.clearResetLinkSentAt();
      }
      if (this.retryCount < 0) {
        this.retryCount = 0;
      }
      if (this.currentScreen === ScreenType.login) {
        this.pin = '';
      } else if (this.currentScreen === ScreenType.validate) {
        this.validatePin = '';
      }
      if (error?.error === 'incorrect_pin' && this.retryCount) {
        return AuthResult.incorrectPin;
      }
      return AuthResult.serverError;
    }
  }

  async back() {
    this.pin = '';
    this.validatePin = '';
    await this.slides.slidePrev();
  }

  async next() {
    if (await this.slides.isEnd()) {
      this.finish();
    }
    this.slides.slideNext();
  }

  async finish() {
    await this.vault.unlockVault();
    this.eventBusSvc.publish(EventTopic.AuthFlowStateEventType, { type: AuthFlowStateEventType.finished });
  }

  async setStatus(status: PincodeNativeStatus = PincodeNativeStatus.enabled) {
    const key = `${this.type}Status`;
    localStorage.setItem(key, status);
    this[this.type].prompted = true;
    if (status === PincodeNativeStatus.skipped) {
      this[this.type].skipped = true;
      if (this.type === AuthType.pincode) {
        await this.profileSvc.setProfileField('pin_code_status', 'skipped');
        this.setPinStatus();
      }
      return await this.finish();
    }
    if (status !== PincodeNativeStatus.prompted) {
      this[this.type].enabled = true;
      this[this.type].prompted = false;
    }
    if (status === PincodeNativeStatus.enabled) {
      this.locked = false;
      this.resetToken = null;
      this.pincode.resetPrompted = false;
    }
    if (status === PincodeNativeStatus.resetPrompted) {
      this.pincode.resetPrompted = true;
    }
  }

  // keep this for reference when we need to implement the native security
  // we should remove it once we have the native security implemented
  // async toggleNative() {
  //   if (this.native.enabled) {
  //     this.vault.state.lockType = 'None';
  //     await this.vault.setLockType();
  //     this._type = AuthType.native;
  //     this.native.enabled = false;
  //     this.setStatus('PROMPTED');
  //   } else {
  //     await this.validate();
  //   }
  // }
  // async validate(redirectHome = true) {
  //   this.vault.state.lockType = 'Native';
  //   await this.vault.setLockType();
  //   await this.vault.validateBio();
  //   this._type = AuthType.native;
  //   this.setStatus();
  //   // await (redirectHome ? this.proceed() : this.next());
  //   await this.next();
  // }

  getFlowStatesByWorkflow = (type: AuthWorkFlow): AuthFlowState[] => {
    const states = [];
    if (type === AuthWorkFlow.loginPin) {
      states.push(AuthFlowState.create(FlowType.login, AuthType.pincode, ScreenType.login, ScreenType.login));
    } else if (type === AuthWorkFlow.setup || type === AuthWorkFlow.setupFromSetting) {
      if (type === AuthWorkFlow.setup) {
        states.push(AuthFlowState.create(FlowType.setup, AuthType.pincode, null, ScreenType.optional));
      } else {
        states.push(AuthFlowState.create(FlowType.setup, AuthType.pincode, null, ScreenType.extra));
      }
      states.push(AuthFlowState.create(FlowType.setup, AuthType.pincode, ScreenType.create, ScreenType.create));
      states.push(AuthFlowState.create(FlowType.setup, AuthType.pincode, ScreenType.create, ScreenType.validate));
      states.push(AuthFlowState.create(FlowType.setup, AuthType.pincode, null, ScreenType.success));
    } else if (type === AuthWorkFlow.change) {
      states.push(AuthFlowState.create(FlowType.change, AuthType.pincode, ScreenType.change, ScreenType.login));
      states.push(AuthFlowState.create(FlowType.change, AuthType.pincode, ScreenType.change, ScreenType.change));
      states.push(AuthFlowState.create(FlowType.change, AuthType.pincode, ScreenType.change, ScreenType.validate));
      states.push(AuthFlowState.create(FlowType.change, AuthType.pincode, null, ScreenType.success));
    } else if (type === AuthWorkFlow.locked) {
      states.push(AuthFlowState.create(FlowType.locked, AuthType.pincode, ScreenType.reset, ScreenType.locked));
    } else if (type === AuthWorkFlow.reset) {
      states.push(AuthFlowState.create(FlowType.reset, AuthType.pincode, ScreenType.create, ScreenType.create));
      states.push(AuthFlowState.create(FlowType.reset, AuthType.pincode, ScreenType.create, ScreenType.validate));
      states.push(AuthFlowState.create(FlowType.reset, AuthType.pincode, null, ScreenType.success));
    }
    return states;
  };

  getResetFlowState(): AuthFlowState {
    return AuthFlowState.create(FlowType.reset, AuthType.pincode, ScreenType.reset, ScreenType.reset);
  }

  setPropertiesByAuthFlowState(authFlowState: AuthFlowState) {
    this._flowType = authFlowState.flowType;
    this._type = authFlowState.type;
    this._currentScreen = authFlowState.currentScreen;
    if (authFlowState.status) {
      this.setStatus(authFlowState.status);
    }
    if (authFlowState.clearValidatePin) {
      this.validatePin = '';
    }
  }

  async isPincodeEnabled(): Promise<boolean> {
    if (!this.loaded) {
      await this.init();
    }
    const isLoggedIn = await this.authSvc.isLoggedin();
    return isLoggedIn && this.pincode.featureToggled && !this.pincode.skipped;
  }

  async getCurrentWorkflow(type: PincodeModalType = PincodeModalType.auto): Promise<AuthWorkFlow> {
    if (!this.loaded) {
      await this.init();
    }
    if (type === PincodeModalType.changeOrSetup) {
      if (this.pincode.enabled) {
        return AuthWorkFlow.change;
      } else if (this.pincode.skipped) {
        return AuthWorkFlow.setupFromSetting;
      } else {
        return AuthWorkFlow.setup;
      }
    }

    if (this.resetToken) {
      return AuthWorkFlow.reset;
    } else if (this.locked) {
      return AuthWorkFlow.locked;
    } else if (this.pincode.enabled) {
      return AuthWorkFlow.loginPin;
    } else {
      return AuthWorkFlow.setup;
    }
  }

  clearPinStatus() {
    localStorage.removeItem('pincodeStatus');
    localStorage.removeItem('nativeStatus');
    this.clearPin();
  }

  clearPin() {
    this.pin = '';
    this.validatePin = '';
  }

  resetPinStatus() {
    this.clearPin();
    this.clearPinStatus();
    this.setPinStatus();
    this.vault.lockVault();
  }
}
